import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from 'src/shared/components/ui/sheet';
import { Input } from 'src/shared/components/ui/input';
import { Button } from 'src/shared/components/ui/button';
import { useUpdateProjectMutation } from 'src/app/admin/state/api/adminGraphSlice';

type ManageProjectSheetProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    projectId: string;
    projectName: string;
};

/** Where a project's editable details are changed. Today that is its name alone. */
const ManageProjectSheet = ({ open, onOpenChange, projectId, projectName }: ManageProjectSheetProps) => {
    const [updateProject, { isLoading }] = useUpdateProjectMutation();
    const [name, setName] = useState(projectName);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        setName(projectName);
    }, [projectName]);

    const trimmedName = name.trim();
    // A change that changes nothing is not worth sending to a super-admin-only mutation.
    const canSubmit = trimmedName !== '' && trimmedName !== projectName && !isLoading;

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            setName(projectName);
            setErrorMessage(null);
        }
        onOpenChange(nextOpen);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSubmit) {
            return;
        }
        setErrorMessage(null);
        try {
            await updateProject({ projectId, projectName: trimmedName }).unwrap();
            onOpenChange(false);
        } catch {
            setErrorMessage('Something went wrong renaming the project. Please try again.');
        }
    };

    return (
        <Sheet open={open} onOpenChange={handleOpenChange}>
            <SheetContent>
                <SheetHeader>
                    <SheetTitle>Rename project</SheetTitle>
                    <SheetDescription>Change the display name for {projectId}. The Project ID cannot be changed.</SheetDescription>
                </SheetHeader>

                <form onSubmit={handleSubmit} className='flex flex-col gap-4 mt-6' data-testid='manage-project-form'>
                    <div className='flex flex-col gap-1'>
                        <label htmlFor='project-name' className='text-sm font-medium'>
                            Project name
                        </label>
                        <Input id='project-name' value={name} onChange={event => setName(event.target.value)} />
                    </div>

                    {errorMessage && (
                        <p role='alert' className='text-sm text-red-100'>
                            {errorMessage}
                        </p>
                    )}

                    <SheetFooter className='mt-2'>
                        <Button type='button' variant='outline' onClick={() => handleOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type='submit' disabled={!canSubmit} className='flex-grow'>
                            Save
                        </Button>
                    </SheetFooter>
                </form>
            </SheetContent>
        </Sheet>
    );
};

export default ManageProjectSheet;
